var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import React from 'react';
import bookPromise from '~/models/book-titles';
import urlFromSlug from './url-from-slug';
export function transformData(data) {
    Object.keys(data).forEach((prop) => {
        if (Array.isArray(data[prop])) {
            const arr = data[prop];
            const contentItem = arr.filter((e) => e.type === 'content').length === 1;
            if (contentItem) {
                data[prop] = {};
                arr.forEach((v) => {
                    data[prop][v.type] = v.value;
                });
            }
            else {
                data[prop] = arr.map((item) => {
                    if (item.value) {
                        return item.value;
                    }
                    return item;
                });
            }
        }
    });
    return data;
}
export function getUrlFor(initialSlug) {
    return __awaiter(this, void 0, void 0, function* () {
        let apiUrl = urlFromSlug(initialSlug);
        // A little magic to handle book titles
        if (initialSlug.startsWith('books/') && initialSlug.length > 6) {
            const strippedSlug = initialSlug.substring(6);
            const bookList = yield bookPromise;
            const bookEntry = bookList.find((e) => e.meta.slug === strippedSlug);
            if (bookEntry) {
                apiUrl = bookEntry.meta.detail_url;
            }
        }
        const qsChar = (/\?/).test(apiUrl) ? '&' : '?';
        const draftParams = new URLSearchParams(window.location.search).has('preview')
            ? `&draft=${Date.now()}`
            : '';
        return `${apiUrl}${qsChar}format=json${draftParams}`;
    });
}
function camelCase(underscored) {
    return underscored.replace(/_+([a-z0-9])/g, (_, chr) => chr.toUpperCase());
}
export function camelCaseKeys(obj) {
    if (!(obj instanceof Object)) {
        return obj;
    }
    if (obj instanceof Array) {
        return obj.map((v) => camelCaseKeys(v));
    }
    return Object.keys(obj).reduce((result, k) => {
        result[camelCase(k)] = camelCaseKeys(obj[k]);
        return result;
    }, {});
}
export function fetchFromCMS(slug_1) {
    return __awaiter(this, arguments, void 0, function* (slug, preserveWrapping = false) {
        const apiUrl = yield getUrlFor(slug);
        let data;
        try {
            data = yield fetch(apiUrl).then((response) => {
                if (response.status === 404) {
                    return { error: new Error('page not found') };
                }
                return response.json();
            });
        }
        catch (err) {
            console.warn(`ERROR fetching slug ${slug}: ${err}`);
            data = { error: err };
        }
        data.slug = slug;
        return preserveWrapping || data.error ? data : transformData(data);
    });
}
export function useTextFromSlug(slug) {
    const [text, setText] = React.useState();
    const [head, setHead] = React.useState();
    React.useEffect(() => {
        const url = urlFromSlug(slug);
        fetch(url)
            .then((r) => {
            if (r === null || r === void 0 ? void 0 : r.ok) {
                r.text().then((pageHtml) => {
                    var _a, _b;
                    const parser = new window.DOMParser();
                    const newDoc = parser.parseFromString(pageHtml, 'text/html');
                    setText(newDoc.body.innerHTML);
                    setHead({
                        title: (_a = newDoc.head.querySelector('title')) === null || _a === void 0 ? void 0 : _a.textContent,
                        description: (_b = newDoc.head
                            .querySelector('[name="description"]')) === null || _b === void 0 ? void 0 : _b.getAttribute('content')
                    });
                });
            }
            else {
                setText(new Error((r === null || r === void 0 ? void 0 : r.statusText) || `Failed to load ${slug}`));
            }
        })
            .catch((err) => setText(err));
    }, [slug]);
    return { head, text };
}
export function useDataFromPromise(promise, defaultValue) {
    const [data, setData] = React.useState(defaultValue);
    React.useEffect(() => {
        let cancelled = false;
        if (promise) {
            // Swallow rejections; a failed fetch reads the same as "no data"
            promise.then((resolved) => {
                if (!cancelled) {
                    setData(resolved);
                }
            }, () => {
                if (!cancelled) {
                    setData(null);
                }
            });
        }
        else {
            setData(null);
        }
        return () => {
            cancelled = true;
        };
    }, [promise]);
    return data;
}
export function useDataFromSlug(slug, preserveWrapping = false) {
    const promise = React.useMemo(() => (slug ? fetchFromCMS(slug, preserveWrapping) : null), [slug, preserveWrapping]);
    return useDataFromPromise(promise);
}
